import { useContext, useEffect, useRef, useState } from 'react';
import { AppleGooglePlayTypes } from '@msc-b2c/interfaces/types';
import { useDispatch } from 'react-redux';
import {
  closeLoader,
  errorModalOpen,
  getAllowedCardPaymentButtons,
  getErrorMessage,
  getPaymentButtons,
  myAreaFinalizeBookingPayment,
  PaymentContext,
  useScript,
} from '@msc-b2c/shared-utils';

const DEFAULT_OPTIONS = {
  tokenOnly: false,
  autoSettle: false,
  useGooglePay: getPaymentButtons().find((paymentButton) => paymentButton.type === 'PAY'),
  useApplePay: getPaymentButtons().find((paymentButton) => paymentButton.type === 'APL'),
  googlePayConfiguration: {
    buttonType: 'long',
    buttonStyle: 'black',
    merchantId: '{googlePayMerchantId}',
  },
  applePayConfiguration: {
    buttonType: 'plain',
    buttonStyle: 'black',
  },
};

export const useAppleOrGooglePay = ({
  merchantId,
  gpayMerchantId,
  datatransPaymentButtonUrl,
  merchantName,
  options,
  payment,
  paymentType,
}: AppleGooglePlayTypes) => {
  const [error, setError] = useState('');
  const [isLoaded, hasError] = useScript({ src: datatransPaymentButtonUrl });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const payButton = useRef(null);
  const paymentButton = globalThis?.PaymentButton;

  const { onFinalizePayment } = useContext(PaymentContext);

  const defaultOptions = {
    ...DEFAULT_OPTIONS,
    getAllowedCardPaymentButtons,
    googlePayConfiguration: {
      ...DEFAULT_OPTIONS.googlePayConfiguration,
      merchantId: gpayMerchantId || '{googlePayMerchantId}',
    },
  };

  const dispatch = useDispatch();

  const cleanupPaymentButton = () => {
    if (paymentButton) {
      globalThis.setTimeout(() => {
        try {
          paymentButton.destroy();
        } catch (err) {
          console.log('Unmount error', err);
        }
      }, 1);
    }
  };

  useEffect(() => {
    if (isLoaded && !hasError && paymentButton && payButton) {
      setError('');
      const handleInit = () => {
        paymentButton.create(payButton.current, payment);
      };

      const handleError = (err: unknown) => {
        console.log('ERROR on payment btn', err);
        const message = getErrorMessage(err);
        setError(message);
      };

      const handleAuth = (response: { transactionId: string }) => {
        console.log('response: ', response);

        if (response?.transactionId) {
          setPaymentLoading(true);

          onFinalizePayment?.(
            { transactionId: response.transactionId, type: paymentType },
            (res: any) => {
              if (res) {
                dispatch(myAreaFinalizeBookingPayment(res));
                closeLoader('.loader');
                console.log('Finalize payment response:', res);
                // Navigate to Thank You page
              } else {
                dispatch(errorModalOpen(true));
              }
            },
            (error: any) => {
              console.log('Finalize payment response - Error', error);
              dispatch(errorModalOpen(true));
            },
            () => {
              setPaymentLoading(false);
            }
          );
        }
      };

      // Add event listeners
      paymentButton.on('init', handleInit);
      paymentButton.on('error', handleError);
      paymentButton.on('authorization', handleAuth);

      // Initialize payment button with options
      paymentButton.init({
        ...defaultOptions,
        ...options,
        merchantId,
        merchantName,
      });

      // Cleanup function
      return () => {
        // Check if paymentButton still exists before trying to destroy it
        if (paymentButton) {
          console.log('Destroying payment button');
          paymentButton.off('init', handleInit);
          paymentButton.off('error', handleError);
          paymentButton.off('authorization', handleAuth);

          console.log('paymentButton', paymentButton);
          try {
            cleanupPaymentButton();
          } catch (error) {
            console.error('Error during destroy:', error);
          }
        }
      };
    }

    return () => {};
  }, [isLoaded, hasError, paymentButton, payButton]);

  return { payButton, paymentLoading, error };
};
